import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import useNotificationPreferences from '../../hooks/useNotificationPreferences';
import notificationPreferencesService from '../../services/notificationPreferencesService';
import adminNotificationTypes from '../../data/adminNotificationTypes';
import ButtonLoader from '../ButtonLoader';

const AdminNotificationPreferences = () => {
  const { data, isLoading, mutate } = useNotificationPreferences();
  const [preferences, setPreferences] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data?.preferences) {
      setPreferences(data.preferences);
    }
  }, [data]);

  const getPreference = (type, channel) => {
    const pref = preferences[type];
    if (!pref) return channel === 'inApp';
    return pref[channel] ?? (channel === 'inApp');
  };

  const handleToggle = (type, channel) => {
    setPreferences(prev => ({
      ...prev,
      [type]: {
        inApp: getPreference(type, 'inApp'),
        email: getPreference(type, 'email'),
        [channel]: !getPreference(type, channel)
      }
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await notificationPreferencesService.updateNotificationPreferences({ preferences });
      await mutate();
      toast.success('Preferences saved');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500 p-4">Loading...</p>;

  return (
    <div className="p-4">
      <p className="text-sm text-gray-500 mb-6">Choose how you want to receive each type of notification.</p>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-gray-200">
            <th className="pb-3 font-semibold">Notification</th>
            <th className="pb-3 font-semibold text-center w-20">In-App</th>
            <th className="pb-3 font-semibold text-center w-20">Email</th>
          </tr>
        </thead>
        <tbody>
          {adminNotificationTypes.map(({ type, label, description }) => (
            <tr key={type} className="border-b border-gray-100">
              <td className="py-3 pr-4">
                <p className="font-medium">{label}</p>
                <p className="text-gray-500 text-xs mt-0.5">{description}</p>
              </td>
              <td className="text-center">
                <input
                  type="checkbox"
                  checked={getPreference(type, 'inApp')}
                  onChange={() => handleToggle(type, 'inApp')}
                  className="h-4 w-4 accent-ase-blue cursor-pointer"
                />
              </td>
              <td className="text-center">
                <input
                  type="checkbox"
                  checked={getPreference(type, 'email')}
                  onChange={() => handleToggle(type, 'email')}
                  className="h-4 w-4 accent-ase-blue cursor-pointer"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="ase-button-primary h-[40px] px-6 mt-6 flex items-center justify-center"
      >
        {isSaving ? <ButtonLoader /> : 'Save preferences'}
      </button>
    </div>
  );
};

export default AdminNotificationPreferences;
